import React, {
  useEffect,
  useState,
  useCallback,
  useContext,
  useMemo,
} from 'react'
import { withRouter } from 'react-router-dom'

import { withStyles } from '@material-ui/core/styles'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import Paper from '@material-ui/core/Paper'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'

import { CampaignsTotals, DashboardsFilters } from '../Components'
import LinearProgressBar from 'Components/LinearProgressBar'
import { PageTitle } from 'Components/PageTitle'
import { SnackBarContext } from 'Components/SnackBar'
import PlaceholderLoader from 'Components/PlaceholderLoader'

import { getMissionType } from 'Containers/Campaigns/utils'
import {
  fetchClientCampaignsList,
  fetchCampaignsDashboardData,
  fetchCountOfCalculatedCampaigns,
} from 'Containers/Campaigns/gql.js'

import { STATUS_CAMPAIGN_ENUM_ARCHIVE } from '../../../shared/constants'

import { formatNumber, getYearlyCampaignsData, getExportContent } from './util'
import { urlGet, urlSet, urlRemove } from 'Utils/URL'
import { downloadStringAsFile } from 'Utils/Misc/index'

import { LIST_VIEW, MENU_ITEMS, STATUS_FILTER_OPTIONS } from '../constants'
import { CAMPAIGN_ENUM_TYPES } from '../../../shared/constants'

import style from './style.js'

const MonthlyView = ({ classes, clientId, ...props }) => {
  let controller

  const runSnackBar = useContext(SnackBarContext)

  const [campaignCalculatedData, setCampaignCalculatedData] = useState([])
  const [renderData, setRenderData] = useState({})
  const [exportContent, setExportContent] = useState('')
  const [campaignsTotalCount, setCampaignsTotalCount] = useState(null)
  const [aborted, setAborted] = useState(false)
  const [loaderValue, setLoaderValue] = useState(0)
  const [clientCampaignsList, setClientCampaignsList] = useState([])
  const [selectedFilters, setSelectedFilters] = useState({
    startDate: urlGet('Start Date'),
    endDate: urlGet('End Date'),
    statuses: Array.isArray(urlGet('Status'))
      ? urlGet('Status')
      : [urlGet('Status')] || [null],
    types: urlGet('Type'),
    campaignIds: Array.isArray(urlGet('Campaign'))
      ? urlGet('Campaign')
      : [urlGet('Campaign')] || [null],
  })

  const campaignsOptions = useMemo(() => {
    return clientCampaignsList
      .sort(
        (a, b) =>
          STATUS_FILTER_OPTIONS.findIndex(option => option.value === a.status) -
          STATUS_FILTER_OPTIONS.findIndex(option => option.value === b.status),
      )
      .reduce((acc, campaign) => {
        if (campaign.status === STATUS_CAMPAIGN_ENUM_ARCHIVE) {
          return acc
        } else if (acc.find(option => option.value === campaign.status)) {
          acc.push({
            label: campaign.name,
            value: campaign.id,
          })
        } else {
          const status = STATUS_FILTER_OPTIONS.find(
            option => option.value === campaign.status,
          )
          acc.push({
            label: status ? status.label : '',
            value: campaign.status,
            isListSubheader: true,
          })
          acc.push({
            label: campaign.name,
            value: campaign.id,
          })
        }
        return acc
      }, [])
  }, [clientCampaignsList])

  const filters = [
    {
      label: 'Start Date',
      key: 'startDate',
      type: 'month',
    },
    {
      label: 'End Date',
      key: 'endDate',
      type: 'month',
    },
    {
      label: 'Status',
      key: 'statuses',
      options: STATUS_FILTER_OPTIONS,
      type: 'select',
      multiple: true,
    },
    {
      label: 'Type',
      key: 'types',
      type: 'select',
      options: [
        {
          label: 'All',
          value: null,
        },
        {
          label: 'Hand to hand',
          value: CAMPAIGN_ENUM_TYPES.HANDTOHAND,
        },
        {
          label: 'Letterbox',
          value: CAMPAIGN_ENUM_TYPES.LETTERBOX,
        },
        {
          label: 'Direct Mail',
          value: CAMPAIGN_ENUM_TYPES.DIRECTMAIL,
        },
      ],
    },
    {
      label: 'Campaign',
      key: 'campaignIds',
      type: 'select',
      options: [
        ...[
          {
            label: 'All',
            value: null,
          },
        ],
        ...campaignsOptions,
      ],
      multiple: true,
    },
  ]

  const loadingTableRow = (
    <TableRow>
      <TableCell>
        <PlaceholderLoader />
      </TableCell>
      <TableCell align="center">
        <PlaceholderLoader />
      </TableCell>
      <TableCell align="center">
        <PlaceholderLoader />
      </TableCell>
      <TableCell align="center">
        <PlaceholderLoader />
      </TableCell>
      <TableCell align="center">
        <PlaceholderLoader />
      </TableCell>
      <TableCell align="center">
        <PlaceholderLoader />
      </TableCell>
      <TableCell align="center">
        <PlaceholderLoader />
      </TableCell>
      <TableCell align="center">
        <PlaceholderLoader />
      </TableCell>
    </TableRow>
  )

  const renderLoadingTableRow = rowsCount => {
    return new Array(rowsCount).fill().map(() => loadingTableRow)
  }

  const renderLoadingTable = () => (
    <Table className={classes.table} size="small">
      <TableHead>
        <TableRow>
          <TableCell>Months</TableCell>
          <TableCell align="center">Flyers Distributed</TableCell>
          <TableCell align="center">Scans</TableCell>
          <TableCell align="center">Total Cost</TableCell>
          <TableCell align="center">Printing Cost</TableCell>
          <TableCell align="center" style={{ width: '150px' }}>
            <PlaceholderLoader />
          </TableCell>
          <TableCell align="center" style={{ width: '150px' }}>
            <PlaceholderLoader />
          </TableCell>
          <TableCell align="center" style={{ width: '150px' }}>
            <PlaceholderLoader />
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        <TableRow style={{ backgroundColor: '#F5F5F5', height: '33px' }}>
          <TableCell />
          <TableCell />
          <TableCell />
          <TableCell />
          <TableCell />
          <TableCell />
          <TableCell />
          <TableCell />
        </TableRow>
        {renderLoadingTableRow(2)}
      </TableBody>
    </Table>
  )

  const getCountOfCalculatedCampaigns = async () => {
    return await fetchCountOfCalculatedCampaigns()
  }

  const fetchCampaignCalculatedData = async (
    newVariables = {},
    newData = [],
  ) => {
    const isCalculatedCampaignsDataExists = await getCountOfCalculatedCampaigns()

    controller = new AbortController()
    const variables = {
      clientIds: [clientId],
      ...selectedFilters,
      campaignIds:
        selectedFilters.campaignIds[0] !== null
          ? selectedFilters.campaignIds
          : undefined,
      statuses:
        selectedFilters.statuses[0] !== null ? selectedFilters.statuses : null,
      types: selectedFilters.types ? [selectedFilters.types] : null,
      first: isCalculatedCampaignsDataExists ? 50 : 3,
      skip: 0,
      ...newVariables,
    }
    try {
      const {
        totalCount,
        hasNextPage,
        data,
      } = await fetchCampaignsDashboardData(variables, controller.signal)
      setCampaignsTotalCount(totalCount)

      const newCampaignCalculatedData = [...newData, ...data]
      setCampaignCalculatedData(newCampaignCalculatedData)

      if (hasNextPage) {
        fetchCampaignCalculatedData(
          {
            skip: (variables.skip || 0) + variables.first,
          },
          newCampaignCalculatedData,
        )
      }
    } catch (error) {
      // console.log(error)
    }
  }

  const campaignsTotalMenuItems = [
    {
      label: `Change to ${LIST_VIEW.CAMPAIGN} display`,
      value: MENU_ITEMS.changeListView,
      handleClick: () => switchToCampaignView(),
    },
    {
      label: 'Export in a CSV',
      value: 'export',
      handleClick: () => handleExport(),
    },
  ]

  const handleExport = () => {
    if (isLoading) {
      return runSnackBar({
        type: 'warning',
        msg: `Please wait until data is loading`,
      })
    }
    downloadStringAsFile(exportContent, 'Campaign.csv', 'text/csv')
  }

  const switchToCampaignView = () =>
    props.history.push(
      `${props.match.path.split(':type')[0]}${LIST_VIEW.CAMPAIGN}`,
    )

  const handleFiltersChange = useCallback(
    (key, label, value) => {
      setCampaignsTotalCount(null)
      setCampaignCalculatedData([])
      setLoaderValue(0)
      setRenderData({})
      setAborted(true)
      controller.abort()

      if (label === 'Campaign' || label === 'Status') {
        if (value.length && value[value.length - 1] !== null) {
          const newValue = value.filter(item => item !== null)
          urlSet(label, newValue)
          return setSelectedFilters({
            ...selectedFilters,
            ...{
              [key]: value.filter(item => item !== null),
            },
          })
        }
        urlRemove(label)
        return setSelectedFilters({
          ...selectedFilters,
          [key]: [null],
        })
      }

      if (!value) {
        urlRemove(label)
      } else {
        urlSet(label, value)
      }
      setSelectedFilters({
        ...selectedFilters,
        ...{ [key]: value },
      })
    },
    [selectedFilters, controller],
  )

  const fetchClientCampaignsListData = async () => {
    const clientCampaignsListResponse = await fetchClientCampaignsList({
      clientIds: [clientId],
    })
    setClientCampaignsList(clientCampaignsListResponse)
  }

  useEffect(() => {
    fetchCampaignCalculatedData()
    fetchClientCampaignsListData()

    return () => {
      controller.abort()
    }
  }, [])

  useEffect(() => {
    if (aborted) {
      setAborted(false)
      fetchCampaignCalculatedData()
    }
  }, [aborted])

  useEffect(() => {
    if (
      campaignsTotalCount !== 0 &&
      campaignsTotalCount === campaignCalculatedData.length
    ) {
      const {
        yearlyCampaignsData,
        metricsValuesForGroupedMissions,
      } = getYearlyCampaignsData(campaignCalculatedData)
      setRenderData(yearlyCampaignsData)
      setExportContent(
        getExportContent({
          yearlyCampaignsData,
          metricsValuesForGroupedMissions,
        }),
      )
    }
  }, [campaignCalculatedData, campaignsTotalCount])

  const isLoading = useMemo(
    () =>
      campaignCalculatedData.length < campaignsTotalCount ||
      (!campaignsTotalCount && campaignsTotalCount !== 0),
    [campaignCalculatedData, campaignsTotalCount],
  )

  useEffect(() => {
    if (
      ((campaignCalculatedData.length / campaignsTotalCount) * 100 < 99 &&
        loaderValue === 99) ||
      !campaignsTotalCount
    )
      return

    if ((campaignCalculatedData.length / campaignsTotalCount) * 100 < 99) {
      setTimeout(
        () => {
          setLoaderValue(loaderValue + 1)
        },
        loaderValue < 40
          ? 200
          : loaderValue < 60
          ? 500
          : loaderValue < 85
          ? 100
          : 1000,
      )
    } else {
      setLoaderValue(
        (campaignCalculatedData.length / campaignsTotalCount) * 100,
      )
    }
  }, [campaignCalculatedData, campaignsTotalCount, loaderValue])

  return (
    <Box className={classes.dashboardsWrapper}>
      <PageTitle>Campaigns</PageTitle>

      <DashboardsFilters
        filters={filters}
        selectedFilters={selectedFilters}
        handleFiltersChange={handleFiltersChange}
      />

      <CampaignsTotals
        campaignsTotalCount={campaignsTotalCount}
        campaignCalculatedData={campaignCalculatedData}
        menuItems={campaignsTotalMenuItems}
      />
      {Object.keys(renderData).length &&
      campaignCalculatedData.length === campaignsTotalCount ? (
        Object.entries(renderData).map(([type, years]) => {
          const yearsWithoutMetrics = Object.entries(years).reduce(
            (acc, [year, months]) => {
              if (year !== 'selectedMetrics') {
                const month = Object.keys(months)[0]
                acc.push({
                  year: year.split('y')[0],
                  columsCount:
                    years.selectedMetrics.length * 3 +
                    Object.keys(months[month]).length,
                })
                Object.entries(months).forEach(([month, values]) =>
                  acc.push({ ...values, month: month }),
                )
              }
              return acc
            },
            [],
          )
          return (
            <Box className={classes.tableWrapper}>
              <Typography paragraph className={classes.borderBottom}>
                {getMissionType(type)}
              </Typography>
              <TableContainer component={Paper}>
                <Table className={classes.table} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Months</TableCell>
                      <TableCell align="center">Flyers Distributed</TableCell>
                      {years.selectedMetrics.map(metric => (
                        <TableCell align="center">{metric}</TableCell>
                      ))}
                      <TableCell align="center">Scans</TableCell>
                      <TableCell align="center">Total Cost</TableCell>
                      <TableCell align="center">Printing Cost</TableCell>
                      {years.selectedMetrics.map(metric => (
                        <TableCell align="center">{`CP ${metric}`}</TableCell>
                      ))}
                      {years.selectedMetrics.map((metric, index) => (
                        <TableCell
                          align={
                            index === years.selectedMetrics.length - 1
                              ? 'right'
                              : 'center'
                          }
                        >
                          {`${metric} CVR`}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {yearsWithoutMetrics.map(value => {
                      if (value.year) {
                        return (
                          <TableRow style={{ backgroundColor: '#F5F5F5' }}>
                            {new Array(value.columsCount)
                              .fill(value.columsCount)
                              .map((item, index) => (
                                <TableCell>
                                  {index === 0 ? <b>{value.year}</b> : null}
                                </TableCell>
                              ))}
                          </TableRow>
                        )
                      }
                      return (
                        <TableRow key={value.month}>
                          <TableCell>{value.month}</TableCell>
                          <TableCell align="center">
                            {formatNumber(value.totalFlyersDistributed)}
                          </TableCell>
                          {years.selectedMetrics.map(metric => (
                            <TableCell align="center">
                              {value.metrics[metric]
                                ? formatNumber(
                                    value.metrics[metric].metricValue,
                                  )
                                : '-'}
                            </TableCell>
                          ))}
                          <TableCell align="center">
                            {formatNumber(value.amountScans)}
                          </TableCell>
                          <TableCell align="center">
                            {value.hardCosts
                              ? `$${formatNumber(value.hardCosts)}`
                              : 0}
                          </TableCell>
                          <TableCell align="center">
                            {value.hardCosts
                              ? `$${formatNumber(value.printingCosts)}`
                              : 0}
                          </TableCell>
                          {years.selectedMetrics.map(metric => (
                            <TableCell align="center">
                              {value.metrics[metric] &&
                              value.metrics[metric].costPerMetric
                                ? `$${formatNumber(
                                    value.metrics[metric].costPerMetric,
                                  )}`
                                : 'N/A'}
                            </TableCell>
                          ))}
                          {years.selectedMetrics.map((metric, index) => (
                            <TableCell
                              align={
                                index === years.selectedMetrics.length - 1
                                  ? 'right'
                                  : 'center'
                              }
                            >
                              {value.metrics[metric] &&
                              value.metrics[metric].conversionRate
                                ? `${formatNumber(
                                    value.metrics[metric].conversionRate,
                                  )}%`
                                : 'N/A'}
                            </TableCell>
                          ))}
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )
        })
      ) : (
        <Box className={classes.loadingWrapper}>
          {campaignsTotalCount === 0 ||
          (campaignCalculatedData.length === campaignsTotalCount &&
            !Object.keys(renderData).length) ? (
            <Typography paragraph>Found 0 D2D/H2H campaigns</Typography>
          ) : null}
          {isLoading ? (
            <Box className={classes.tableWrapper}>
              <Box style={{ margin: '24px 0 24px -16px' }}>
                <LinearProgressBar
                  variant="determinate"
                  value={loaderValue}
                  isLoading={!Object.keys(campaignCalculatedData).length}
                />
              </Box>
              <TableContainer component={Paper}>
                {renderLoadingTable()}
              </TableContainer>
            </Box>
          ) : null}
        </Box>
      )}
    </Box>
  )
}

export default withRouter(withStyles(style)(MonthlyView))
